"use client";

import { useState } from "react";
import { ChipGroup, fieldLabelClass, inputClass } from "@/components/travel/FormChips";

const SUPPORT_EMAIL = "nohayasite@gmail.com";

const EXPERIENCE_OPTIONS = ["Entry Level", "Mid-Level", "Senior", "Executive"].map((v) => ({ value: v, label: v }));
const SERVICE_OPTIONS = ["New Resume", "Resume Rewrite / Edit", "LinkedIn Profile", "Resume + Cover Letter Bundle"].map((v) => ({
  value: v,
  label: v,
}));

type Status = "idle" | "validation-error" | "submitting" | "saved" | "backend-failed";

export default function ResumeServiceForm() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [experience, setExperience] = useState("Mid-Level");
  const [service, setService] = useState("New Resume");
  const [details, setDetails] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  function openFallbackMailto() {
    const subject = `[Nohaya] Resume Service Request — ${fullName.trim()}`;
    const bodyLines = [
      `Name: ${fullName.trim()}`,
      `Email: ${email.trim()}`,
      phone.trim() ? `Phone: ${phone.trim()}` : null,
      jobTitle.trim() ? `Current / Target Job Title: ${jobTitle.trim()}` : null,
      `Experience Level: ${experience}`,
      `Service Needed: ${service}`,
      "",
      "Additional Details:",
      details.trim() || "(none provided)",
    ].filter((line): line is string => line !== null);

    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
    window.location.href = mailto;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      setStatus("validation-error");
      return;
    }

    setStatus("submitting");

    // The backend save is the reliable path -- it works even for visitors
    // with no email app configured. The mailto open below is a best-effort
    // extra, attempted either way, since we can't detect whether it
    // actually succeeded (no email client vs. one that opened silently).
    let backendOk = false;
    try {
      const res = await fetch("/api/resume-services", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          jobTitle: jobTitle.trim() || undefined,
          experienceLevel: experience,
          serviceNeeded: service,
          details: details.trim() || undefined,
        }),
      });
      backendOk = res.ok;
    } catch {
      backendOk = false;
    }

    openFallbackMailto();
    setStatus(backendOk ? "saved" : "backend-failed");
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl border border-slate-200/80 bg-white/80 p-6 backdrop-blur-sm dark:border-slate-800 dark:bg-[#0f172a]/80">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Full Name *</label>
          <input type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={fieldLabelClass}>Email *</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Phone</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={fieldLabelClass}>Current / Target Job Title</label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Software Engineer"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={fieldLabelClass}>Experience Level</label>
        <div className="mt-1.5"><ChipGroup options={EXPERIENCE_OPTIONS} value={experience} onChange={setExperience} /></div>
      </div>

      <div>
        <label className={fieldLabelClass}>Service Needed</label>
        <div className="mt-1.5"><ChipGroup options={SERVICE_OPTIONS} value={service} onChange={setService} /></div>
      </div>

      <div>
        <label className={fieldLabelClass}>Anything Else We Should Know?</label>
        <textarea
          rows={4}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Target job, industry, deadline, current resume issues..."
          className={inputClass}
        />
      </div>

      {status === "validation-error" ? (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">
          Please fill in your name and email so we know who to reply to.
        </p>
      ) : null}
      {status === "saved" ? (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
          Got it — your request has been received and we&apos;ll be in touch. We also tried opening your email app as
          a backup; feel free to send that too if it popped up.
        </p>
      ) : null}
      {status === "backend-failed" ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
          We couldn&apos;t save your request automatically, but we tried opening your email app instead — please send
          that email so we don&apos;t miss you, or email us directly at {SUPPORT_EMAIL}.
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "submitting" ? "Sending..." : "Send My Request"}
      </button>
      <p className="text-center text-xs text-slate-400 dark:text-slate-500">
        Your request is saved directly, whether or not you have an email app set up.
      </p>
    </form>
  );
}
